import React, { useEffect, useMemo, useRef, useState } from "react";

export type TOption = {
  index: number;
  label: string;
  value: string;
}

type TProps = {
  label?: string;
  options: TOption[];
  selected?: TOption;
  onSelect?: (option: TOption) => void;
  placeholder?: string;
  uniqueOptions?: boolean;
};

export default function Autocomplete({ label, options, selected, onSelect, placeholder, uniqueOptions }: TProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const idRef = useRef(`autocomplete-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  
  useEffect(() => {
    if (selected) {
      setInputValue(selected.value);
    } else {
      setInputValue("");
    }
  }, [selected]);


  const handleSelect = (option: TOption) => {
    onSelect?.(option);
    setInputValue(option.label);
    setOpen(false);
  }

  const filteredOptions = useMemo(() => {
    if (uniqueOptions) {
      const seen = new Set();
      options = options.filter(option => {
        const duplicate = seen.has(option.value);
        seen.add(option.value);
        return !duplicate;
      });
    }

    if (!inputValue) {
      return options;
    }

    const filtered = options.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    return filtered;
  }, [options, inputValue, uniqueOptions]);

  const handleInputFocus = () => {
    setIsInputFocused(true);
    if (!open) {
      setOpen(true);
    }
  }

  const isExpanded = useMemo(() => {
    return isInputFocused || !!selected || !!placeholder
  }, [isInputFocused, selected, placeholder])

  return (
    <div className="vitrine-template8__autocomplete" ref={rootRef}>
      <div className="vitrine-template8__autocomplete__trigger">
        {label && <label htmlFor={idRef.current} className={`vitrine-template8__autocomplete__label ${isExpanded ? '--expanded' : ''}`}>{label}</label>}
        <input
          type="text"
          id={idRef.current}
          className="vitrine-template8__autocomplete__input"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={handleInputFocus}
          onBlur={() => setIsInputFocused(false)}
          placeholder={placeholder}
          aria-expanded={open}
        />
      </div>

      {open && (
        <div className="vitrine-template8__autocomplete__menu" role="menu">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <button
                key={opt.index}
                className={`vitrine-template8__autocomplete__item ${selected?.index === opt.index ? "selected" : ""}`}
                role="menuitem"
                onClick={() => handleSelect(opt)}
                type="button"
              >
                <div className="vitrine-template8__autocomplete__item__label">
                  <tgr-text-label size="md-2">{opt.label}</tgr-text-label>
                </div>
                {selected?.index === opt.index && (
                  <tgr-icon size="sm" data-testid="tgr-popover-menu-item__icon-done" icon="done"></tgr-icon>
                )}
              </button>
            ))
          ) : (
            <></>
          )}
        </div>
      )}
    </div>
  );
}
